import React, { useState, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { removeUserSession, getUser } from '../utils/Common';
import { getEnvironmentInfo } from '../utils/axios';
import Swal from 'sweetalert2';
import InstallPrompt from './InstallPrompt';
import logo from '../assets/logo.png';
import { 
  LayoutDashboard, 
  Calendar, 
  TrendingUp, 
  FileText, 
  ClipboardList, 
  ShoppingCart, 
  Package, 
  Users, 
  Building2,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Sun,
  Moon
} from 'lucide-react';

function Layout({ children, title = "Dashboard", subtitle = "" }) {
  const history = useHistory();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const envInfo = getEnvironmentInfo();
  
  // Theme management
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Apply theme on mount
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Connection monitoring
  useEffect(() => {
    let alertShown = false;

    const handleOnline = () => {
      if (alertShown) {
        Swal.fire({
          icon: 'success',
          title: 'Connection Restored',
          text: 'Your internet connection has been restored.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        alertShown = false;
      }
    };

    const handleOffline = () => {
      alertShown = true;
      Swal.fire({
        icon: 'error',
        title: 'Connection Lost',
        text: 'No internet connection. Please check your network.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timerProgressBar: true,
        background: '#fee',
        customClass: {
          popup: 'colored-toast'
        }
      });
    };

    // Check initial connection status
    if (!navigator.onLine) {
      handleOffline();
    }

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768; // md breakpoint
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(true);
        setMobileMenuOpen(false);
      } else {
        setSidebarCollapsed(false);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Session data
  const userData = getUser() || {};
  const role = userData.role_name;
  
  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Calendar, label: 'Daily Flow', path: '/daily' },
    { icon: TrendingUp, label: 'Statistics', path: '/statistics' },
    { icon: Building2, label: 'Sites', path: '/sites' },
    { icon: FileText, label: 'Proposal', path: '/waiting/proposal' },
    { icon: ClipboardList, label: 'Request', path: '/waiting/request' },
    { icon: ShoppingCart, label: 'Purchase Order', path: '/purchase/order' },
    { icon: Package, label: 'Items', path: '/items' },
    { icon: Building2, label: 'Suppliers', path: '/suppliers' },
    { icon: Users, label: 'Users', path: '/users' },
    
    // { 
    //   icon: ShoppingCart, 
    //   label: ' Orders', 
    //   submenu: [
    //     { label: 'All Orders', path: '/purchase/order' },
    //     { label: 'Sent Orders', path: '/purchase/order/sent' },
    //     { label: 'Receive Items', path: '/receive/item' },
    //   ]
    // },
    // { 
    //   icon: '📦', 
    //   label: 'Items', 
    //   submenu: [
    //     { label: 'All Items', path: '/list/item' },
    //     { label: 'Add Item', path: '/add/item' },
    //     { label: 'Waiting Items', path: '/list/item/waiting' },
    //   ]
    // },
    // { 
    //   icon: '📈', 
    //   label: 'Reports', 
    //   submenu: [
    //     { label: 'Proposal Report', path: '/pp/report' },
    //     { label: 'Request Report', path: '/pr/report' },
    //     { label: 'Order Report', path: '/po/report' },
    //     { label: 'Item Report', path: '/item/report' },
    //   ]
    // },
    // { 
    //   icon: '👥', 
    //   label: 'Suppliers', 
    //   submenu: [
    //     { label: 'All Suppliers', path: '/list/supplier' },
    //     { label: 'Add Supplier', path: '/add/supplier' },
    //   ]
    // },
    // { icon: '⚙️', label: 'Settings', path: '/account/setting' },
  ];

  const [expandedMenus, setExpandedMenus] = useState({});

  const toggleSubmenu = (index) => {
    setExpandedMenus(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const isActive = (path) => {
    if (!path) return false; // Safe check for undefined paths
    return window.location.pathname === path;
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-700">
      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: Fixed Left, Mobile: Slide from Top */}
      <div className={`
        ${isMobile ? 'fixed top-0 left-0 right-0 z-50 transform transition-transform duration-300' : 'relative'}
        ${isMobile && !mobileMenuOpen ? '-translate-y-full' : 'translate-y-0'}
        ${!isMobile && (sidebarCollapsed ? 'w-16' : 'w-52')}
        ${isMobile ? 'w-full max-h-[80vh] overflow-y-auto' : ''}
        bg-gradient-to-b from-[#647FBC] via-[#4A63A5] to-[#647FBC] dark:bg-gradient-to-b dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 shadow-lg transition-all duration-300 flex flex-col
      `}>
        {/* Sidebar Header */}
        <div className="p-3 border-b border-secondary-dark">
          <div className={`${sidebarCollapsed && !isMobile ? 'flex justify-center' : 'flex items-center justify-between'}`}>
            {/* Logo - Centered when expanded, centered when collapsed */}
            <div className={`${sidebarCollapsed && !isMobile ? 'hidden' : 'flex-1 flex justify-center'}`}>
              <img src={logo} alt="Dashboard" className="h-16 w-auto object-contain" />
            </div>
            {/* Toggle Button - Only show when not collapsed */}
            {(!sidebarCollapsed || isMobile) && (
              <button 
                onClick={() => {
                  if (isMobile) {
                    setMobileMenuOpen(false);
                  } else {
                    setSidebarCollapsed(!sidebarCollapsed);
                  }
                }}
                className="text-light-gray hover:text-white transition-colors flex-shrink-0"
              >
                {isMobile ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {menuItems.map((item, index) => (
            <div key={index}>
              <button
                onClick={() => {
                  if (item.submenu) {
                    toggleSubmenu(index);
                  } else {
                    // Validate path before navigation
                    if (item.path && item.path.startsWith('/')) {
                      try {
                        history.push(item.path);
                        if (isMobile) setMobileMenuOpen(false);
                      } catch (error) {
                        console.error('Navigation error:', error);
                      }
                    } else {

                    }
                  }
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                  isActive(item.path) 
                    ? 'bg-[#AED6CF] text-primary-dark shadow-sm' 
                    : 'text-light-gray hover:bg-secondary-dark'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {React.createElement(item.icon, { className: "w-4 h-4 flex-shrink-0" })}
                  {(!sidebarCollapsed || isMobile) && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                </div>
                {(!sidebarCollapsed || isMobile) && item.submenu && (
                  <ChevronRight 
                    className={`w-4 h-4 transition-transform ${expandedMenus[index] ? 'rotate-90' : ''}`}
                  />
                )}
              </button>
              
              {/* Submenu */}
              {(!sidebarCollapsed || isMobile) && item.submenu && expandedMenus[index] && (
                <div className="ml-10 mt-1 space-y-1">
                  {item.submenu.map((subItem, subIndex) => (
                    <button
                      key={subIndex}
                      onClick={() => {
                        // Validate path before navigation
                        if (subItem.path && subItem.path.startsWith('/')) {
                          try {
                            history.push(subItem.path);
                            if (isMobile) setMobileMenuOpen(false);
                          } catch (error) {
                          }
                        } else {

                        }
                      }}
                      className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${
                        isActive(subItem.path)
                          ? 'bg-[#AED6CF] text-primary-dark'
                          : 'text-light-gray hover:bg-secondary-dark'
                      }`}
                    >
                      {subItem.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="p-2 border-t border-secondary-dark">
          <button
            onClick={() => {
              removeUserSession();
              history.push('/');
            }}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-accent-rose text-white rounded-lg hover:bg-opacity-80 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {(!sidebarCollapsed || isMobile) && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-700 shadow-sm border-b border-light-gray dark:border-gray-600 px-4 py-2">
          <div className="flex items-center justify-between gap-3">
            {/* Mobile Hamburger Menu */}
            {isMobile && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-light-gray dark:hover:bg-gray-600 rounded-lg transition-colors md:hidden flex-shrink-0"
              >
                <Menu className="w-6 h-6 text-secondary-dark dark:text-light-gray" />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-primary-dark dark:text-light-gray truncate">{title}</h2>
              {subtitle && <p className="text-xs text-secondary-dark dark:text-light-gray/80 mt-0.5 truncate">{subtitle}</p>}
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              {/* Endpoint Status Badge */}
              <div className="hidden sm:flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium" style={{
                backgroundColor: envInfo.isDevelopment ? '#10b981' : '#3b82f6',
                color: 'white'
              }}>
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                <span>{envInfo.label}</span>
              </div>
              
              {/* Theme Toggle Switch */}
              <button 
                onClick={toggleTheme}
                className="relative w-14 h-7 bg-gray-200 dark:bg-gray-600 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-rose"
                title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center ${
                    theme === 'dark' ? 'translate-x-7' : 'translate-x-0'
                  }`}
                >
                  {theme === 'light' ? (
                    <Sun className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <Moon className="w-4 h-4 text-indigo-500" />
                  )}
                </span>
              </button>
              
              {/* User Avatar */}
              <div className="w-8 h-8 bg-gradient-to-r from-accent-rose to-secondary-dark rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg">
                {userData.name ? userData.name.charAt(0).toUpperCase() : 'U'}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800">
          {children}
        </main>
        
        {/* PWA Install Prompt */}
        <InstallPrompt />
      </div>
    </div>
  );
}

export default Layout;
